import { useId } from "react";
import { Check, Crown, Loader2, Sparkles, Zap } from "lucide-react";
import type {
  BillingCurrency,
  Plan,
  PlanAction,
  PlanActionInfo,
  PlanTier,
} from "../../services/billingApi";
import {
  billingPeriodDescription,
  billingPeriodLabel,
  formatCredits,
  formatPrice,
} from "../../data/planRules";

type PlanCardProps = {
  plan: Plan;
  currency: BillingCurrency;
  /**
   * What this customer can do with this plan, from the server.
   * Absent while entitlements load, or for signed-out visitors.
   */
  action?: PlanActionInfo;
  /** Visually promote this card — the recommended pick. */
  isFeatured?: boolean;
  /** Checkout or plan change is in flight for this plan. */
  isBusy?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onSelect: (tier: PlanTier) => void;
};

/** Button label per action, so the card never says "Get" for a downgrade. */
const ACTION_LABEL: Record<PlanAction, (planName: string) => string> = {
  BUY: (name) => `Get ${name}`,
  SUBSCRIBE: (name) => `Subscribe to ${name}`,
  CURRENT: () => "Current plan",
  UPGRADE: (name) => `Upgrade to ${name}`,
  DOWNGRADE: (name) => `Switch to ${name}`,
  UNAVAILABLE: () => "Not available",
};

/** One-line note under the button explaining when the change lands. */
const effectNote = (action: PlanActionInfo): string | null => {
  if (action.action === "UPGRADE") return "Starts immediately";
  if (action.action === "DOWNGRADE") {
    return "Starts when your current period ends";
  }
  return null;
};

const TIER_ICON: Record<PlanTier, typeof Zap> = {
  FREE: Sparkles,
  BASIC: Zap,
  PREMIUM: Sparkles,
  PLATINUM: Crown,
  ULTRA_PRO: Crown,
};

export default function PlanCard({
  plan,
  currency,
  action,
  isFeatured = false,
  isBusy = false,
  disabled = false,
  disabledReason,
  onSelect,
}: PlanCardProps) {
  const reasonId = useId();
  const Icon = TIER_ICON[plan.tier];
  const isCurrent = action?.action === "CURRENT";
  const hasDiscount =
    plan.price > 0 &&
    (plan.discountPercent ?? 0) > 0 &&
    typeof plan.discountedPrice === "number";
  const isActionable =
    plan.purchasable &&
    !disabled &&
    !disabledReason &&
    action !== undefined &&
    action.action !== "CURRENT" &&
    action.action !== "UNAVAILABLE";
  const note = action ? effectNote(action) : null;

  return (
    <div
      className={`relative flex flex-col rounded-3xl border bg-white p-6 transition-all ${
        isFeatured
          ? "border-indigo-300 shadow-xl shadow-indigo-600/10 ring-2 ring-indigo-500/20"
          : "border-gray-200 shadow-sm hover:border-indigo-200 hover:shadow-md"
      }`}
    >
      {isFeatured && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-1 text-[11px] font-black uppercase tracking-wider text-white shadow-md">
          Most popular
        </span>
      )}

      {isCurrent && (
        <span className="absolute -top-3 right-4 rounded-full bg-emerald-600 px-3 py-1 text-[11px] font-black uppercase tracking-wider text-white shadow-md">
          Your plan
        </span>
      )}

      <div className="flex items-center gap-2.5">
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-xl ${
            isFeatured
              ? "bg-indigo-100 text-indigo-600"
              : "bg-gray-100 text-gray-500"
          }`}
        >
          <Icon className="h-4.5 w-4.5" />
        </div>
        <div>
          <h3 className="text-base font-extrabold tracking-tight text-gray-900">
            {plan.name}
          </h3>
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
            {plan.audience}
          </p>
        </div>
      </div>

      {/* A live promotion shows the list price struck through beside the
          discounted figure — the same figure checkout charges. */}
      {hasDiscount && (
        <span className="absolute -top-3 left-4 rounded-full bg-rose-500 px-2.5 py-1 text-[11px] font-black uppercase tracking-wider text-white shadow-md">
          {plan.discountPercent}% off
        </span>
      )}

      <div className="mt-5 flex items-baseline gap-1.5">
        {hasDiscount && (
          <span className="text-base font-bold text-gray-400 line-through">
            {formatPrice(plan.price, currency)}
          </span>
        )}
        <span className="text-3xl font-black tracking-tighter text-gray-900">
          {plan.price === 0
            ? "Free"
            : formatPrice(
                hasDiscount ? (plan.discountedPrice as number) : plan.price,
                currency,
              )}
        </span>
        {plan.price > 0 && (
          <span className="text-xs font-semibold text-gray-400">
            {billingPeriodLabel(plan.billingPeriod)}
          </span>
        )}
      </div>

      <p className="mt-1 text-xs font-bold text-indigo-600">
        {formatCredits(plan.downloadCredits, plan.billingPeriod)}
      </p>

      {plan.price > 0 && (
        <p className="mt-1 text-[11px] font-medium text-gray-400">
          {billingPeriodDescription(plan.billingPeriod)}
        </p>
      )}

      <ul className="mt-5 flex-1 space-y-2.5">
        {plan.highlights.map((highlight) => (
          <li key={highlight} className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
            <span className="text-sm font-medium leading-snug text-gray-600">
              {highlight}
            </span>
          </li>
        ))}
      </ul>

      <div
        className="group relative mt-6 rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600"
        tabIndex={disabledReason ? 0 : undefined}
        role={disabledReason ? "group" : undefined}
        aria-label={
          disabledReason ? `Why ${plan.name} is unavailable` : undefined
        }
        aria-describedby={disabledReason ? reasonId : undefined}
      >
        <button
          type="button"
          disabled={!isActionable || isBusy}
          onClick={() => {
            if (isActionable && !isBusy) onSelect(plan.tier);
          }}
          aria-describedby={disabledReason ? reasonId : undefined}
          className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold transition-all disabled:cursor-not-allowed ${
            isFeatured
              ? "bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-lg shadow-indigo-600/25 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100"
              : "border-2 border-gray-200 bg-white text-gray-700 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 disabled:opacity-50 disabled:hover:border-gray-200 disabled:hover:bg-white disabled:hover:text-gray-700"
          }`}
        >
          {isBusy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Working…
            </>
          ) : !plan.purchasable ? (
            "Included"
          ) : action ? (
            ACTION_LABEL[action.action](plan.name)
          ) : (
            `Get ${plan.name}`
          )}
        </button>
        {disabledReason && (
          <div
            role="tooltip"
            className="pointer-events-none absolute bottom-full left-0 z-20 mb-2 hidden w-full rounded-xl bg-gray-900 p-3 text-xs font-medium leading-relaxed text-white shadow-lg group-hover:block group-focus-visible:block"
          >
            {disabledReason}
          </div>
        )}
      </div>
      {disabledReason && (
        <p
          id={reasonId}
          className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-semibold leading-relaxed text-amber-900"
        >
          {disabledReason}
        </p>
      )}

      {(note ?? action?.reason) && !isBusy && (
        <p
          className={`mt-2 text-center text-[11px] font-semibold ${
            action?.action === "DOWNGRADE" ? "text-amber-600" : "text-gray-400"
          }`}
        >
          {note ?? action?.reason}
        </p>
      )}
    </div>
  );
}
